import merge from 'lodash.merge';
import { PROMISE_PENDING, promiseStatus } from 'promise-status-async';
import { FC, PropsWithChildren, createContext, useContext } from 'react';
import { suspend } from 'suspend-react';
import { StoreApi, createStore, useStore } from 'zustand';
import { ProviderConfiguration } from './provider';

export interface BackstageConfig {
  variables?: {
    [key: string]: string;
  };
  flags?: {
    [key: string]: boolean;
  };
}

const uniqueIDForProviders = (providers: ProviderConfiguration[]): string => {
  return providers
    .map(provider => {
      const configuration = provider();
      return `${configuration.name}-${configuration.priority}`;
    })
    .join('-');
};

const getConfigurations = async (providers: ProviderConfiguration[]) => {
  const configurations = await Promise.all(
    providers.map(async plugin => {
      const provider = plugin();
      const configuration = await provider.get();

      return {
        name: provider.name,
        priority: provider.priority,
        config: configuration
      };
    })
  );
  return configurations.sort((a, b) => a.priority - b.priority).map(configuration => configuration.config);
};

const getConfigurationFromProviders = async (
  providers: ProviderConfiguration[],
  initialConfiguration: BackstageConfig = {}
) => {
  const sortedConfigurations = await getConfigurations(providers);
  return merge({ ...initialConfiguration }, ...sortedConfigurations) as BackstageConfig;
};

interface BackstageInfo {
  name: string;
  configuration?: BackstageConfig;
  process: Promise<void>;
}

interface BackstageActions {
  getFlag: (flag: string) => boolean;
  getVariable: (variable: string) => string | undefined;
  getConfig: () => BackstageConfig | undefined;
  updateConfig: (config: BackstageConfig) => void;
}

export type BackstageStore = BackstageInfo & BackstageActions;
export const BackstageContext = createContext<StoreApi<BackstageStore> | null>(null);

export const createBackstageStore = (
  providers: ProviderConfiguration[],
  config: BackstageConfig = {}
): StoreApi<BackstageStore> =>
  createStore<BackstageStore>((set, get) => ({
    name: uniqueIDForProviders(providers),
    process: (async () => {
      const configuration = await getConfigurationFromProviders(providers, structuredClone(config));
      set({ configuration });
    })(),

    getFlag: (key: string) => {
      const { configuration } = get();
      const flag = configuration?.flags?.[key];
      return !!flag;
    },
    getVariable: (key: string) => {
      const { configuration } = get();
      return configuration?.variables?.[key];
    },
    getConfig: () => {
      const { configuration } = get();
      return structuredClone(configuration);
    },
    updateConfig: (config: BackstageConfig) => {
      set(state => ({
        configuration: merge({}, state.configuration ?? {}, config) as BackstageConfig
      }));
    }
  }));

export const Backstage: FC<PropsWithChildren<{ store: StoreApi<BackstageStore> }>> = ({ children, store }) => {
  return <BackstageContext.Provider value={store}>{children}</BackstageContext.Provider>;
};

type SelectorReturn<S extends (s: BackstageStore) => any> = ReturnType<S>;
export function useBackstage<S extends (s: BackstageStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(BackstageContext);
  if (!context) {
    throw new Error('useBackstage must be used within a Backstage');
  }
  return useStore(context, selector);
}

export function useVariable<K extends string>(key: K) {
  const process = useBackstage(state => state.process);
  const getVariable = useBackstage(state => state.getVariable);
  const name = useBackstage(state => state.name);

  return suspend(async () => {
    const value = getVariable(key);
    // If there is a value or the process is not pending, return the value
    if (value || (await promiseStatus(process)) !== PROMISE_PENDING) return value;
    // If there is ongoing work, wait for it to finish
    await process;
    // Return the value
    return getVariable(key);
  }, [name, key]);
}

/*
 * WEIRD THING THAT CAN HAPPEN WITH THIS FUNCTION
 * 
 * The first time you call it, it locks in the return value from the suspend for those particular keys.
 * 
 * That's great most of the time- it means way less processing time when it's a known result.
 * 
 * But there's a weird bug where if you call this function too early, it returns a boolean, and if you call it
 * after a certain point, it returns an object containing a boolean field.
 * 
 * We can work around this, but if you later try and use the same key with useFeatures, it returns false negatives.
 */ 
export function useFlag<K extends string>(key: K) {
  const process = useBackstage(state => state.process);
  const getFlag = useBackstage(state => state.getFlag);
  const name = useBackstage(state => state.name);

  return suspend(async () => {
    const value = getFlag(key);
    // If there is a value or the process is not pending, return the value
    if (value || (await promiseStatus(process)) !== PROMISE_PENDING) return value;
    // If there is ongoing work, wait for it to finish
    await process;
    // Return the value
    return getFlag(key);
  }, [name, key]);
}
export function useFlags<K extends string>(keys: K[]) {
  const process = useBackstage(state => state.process);
  const getFlag = useBackstage(state => state.getFlag);
  const name = useBackstage(state => state.name);

  return suspend(async () => {
    const values = Object.fromEntries(keys.map(key => [key, getFlag(key)])) as { [key in K]: boolean };
    // If there is a value or the process is not pending, return the value
    if ((await promiseStatus(process)) !== PROMISE_PENDING) return values;
    // If there is ongoing work, wait for it to finish
    await process;
    // Return the value
    return Object.fromEntries(keys.map(key => [key, getFlag(key)])) as { [key in K]: boolean };
  }, [name, ...keys]);
}

export function useConfig() {
  const process = useBackstage(state => state.process);
  const getConfig = useBackstage(state => state.getConfig);
  const name = useBackstage(state => state.name);

  return suspend(async () => {
    const value = getConfig();
    // If there is a value or the process is not pending, return the value
    if (value || (await promiseStatus(process)) !== PROMISE_PENDING) return value;
    // If there is ongoing work, wait for it to finish
    await process;
    // Return the value
    return getConfig();
  }, [name]);
}
